/* BookingWidget → ServicePro booking API client.
 *
 * Reuses the same public booking endpoints the standalone booking app
 * (apps/booking) calls — only the contracts are shared, not the UI. `apiUrl`
 * is passed in from the injected jobsite runtime config (window.__SERVICEPRO_CONFIG__).
 * Each call returns a discriminated result so callers render inline errors
 * without try/catch.
 */

import type { Data } from "@puckeditor/core";
import { EMPTY_FORM_DATA } from "../../forms/form-data";

export type BookingSchedulingMode = "instant" | "request";

export interface BookingService {
  id: string;
  name: string;
  slug: string;
  blurb: string;
  duration: number | null; // minutes
  price: number | null; // dollars; null → "Estimate"
  priceQualifier: string | null;
  deposit: number | null;
  popular: boolean;
  urgent: boolean;
  schedulingMode: BookingSchedulingMode;
}

export interface BookingPro {
  name: string;
  slug: string;
  trade: string | null;
  area: string | null;
  phone: string | null;
}

export interface BookingPublicData {
  pro: BookingPro;
  services: BookingService[];
  /** The booking form's Puck document (empty → default widget). */
  formData: Data;
}

/** `offline` separates "the API is down" from "the API said no" — only the first may park a lead. */
async function postBooking(
  apiUrl: string,
  path: string,
  body: unknown,
): Promise<{ ok: boolean; offline: boolean; data: Record<string, unknown> }> {
  try {
    const res = await fetch(`${apiUrl}/api/booking${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: res.ok, offline: res.status >= 500, data };
  } catch {
    return { ok: false, offline: true, data: {} };
  }
}

export async function fetchBookingPublic(
  apiUrl: string,
  slug: string,
): Promise<{ ok: true; data: BookingPublicData } | { ok: false; offline: boolean }> {
  try {
    const res = await fetch(`${apiUrl}/api/booking/public/${encodeURIComponent(slug)}`);
    if (!res.ok) return { ok: false, offline: res.status >= 500 };
    const json = (await res.json()) as BookingPublicData;
    return {
      ok: true,
      data: {
        pro: json.pro,
        services: json.services ?? [],
        formData: json.formData ?? EMPTY_FORM_DATA,
      },
    };
  } catch {
    return { ok: false, offline: true };
  }
}

/**
 * Upload one form photo: ask our API for a one-time Cloudflare direct-upload URL
 * (plan-gated, rate-limited), PUT the file to Cloudflare, return its public URL.
 */
export async function uploadFormImage(
  apiUrl: string,
  slug: string,
  file: File,
): Promise<{ ok: true; url: string } | { ok: false; error: string }> {
  try {
    const init = await fetch(`${apiUrl}/api/forms/upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug }),
    });
    const initData = (await init.json().catch(() => ({}))) as Record<string, unknown>;
    if (!init.ok) return { ok: false, error: (initData.error as string) ?? "Couldn't upload that photo." };

    const form = new FormData();
    form.append("file", file);
    const up = await fetch(initData.uploadUrl as string, { method: "POST", body: form });
    if (!up.ok) return { ok: false, error: "Couldn't upload that photo." };
    return { ok: true, url: initData.publicUrl as string };
  } catch {
    return { ok: false, error: "Couldn't upload that photo." };
  }
}

export async function sendOtp(
  apiUrl: string,
  phone: string,
  opts: { slug?: string; turnstileToken?: string } = {},
): Promise<{ ok: true } | { ok: false; offline: boolean; error: string; code?: string }> {
  // slug lets the server verify the jobsite's per-site Turnstile token.
  const { ok, offline, data } = await postBooking(apiUrl, "/otp/send", {
    phone,
    slug: opts.slug,
    turnstileToken: opts.turnstileToken,
  });
  return ok
    ? { ok: true }
    : {
        ok: false,
        offline,
        error: (data.error as string) ?? "Couldn't send a code. Please try again.",
        code: data.code as string | undefined,
      };
}

export interface VerifiedCustomer {
  exists: boolean;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export async function verifyOtp(
  apiUrl: string,
  phone: string,
  code: string,
): Promise<
  | { ok: true; verificationId: string; customer: VerifiedCustomer }
  | { ok: false; offline: boolean; error: string }
> {
  const { ok, offline, data } = await postBooking(apiUrl, "/otp/verify", { phone, code });
  return ok
    ? {
        ok: true,
        verificationId: data.verificationId as string,
        customer: data.customer as VerifiedCustomer,
      }
    : { ok: false, offline, error: (data.error as string) ?? "Couldn't verify that code." };
}

export interface CreateBookingBody {
  verificationId: string;
  slug: string;
  serviceId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  consent: boolean;
  /** Ticked "Text me updates about this booking". Optional, never required to book. */
  smsOptIn?: boolean;
  /** Instant mode — an exact start time taken from the availability list. */
  slot?: { startsAt: string };
  request?: { preferredDate?: string; window: string; note?: string };
  /** Form-builder custom field values, keyed by field.key. Stored in formData. */
  customFields?: Record<string, unknown>;
  /** One-line service address, as the customer gave it. */
  address?: string;
  sessionId?: string | null;
}

export interface BookingResult {
  id: string;
  status: string;
  confCode: string;
  serviceName: string;
}

export async function createBooking(
  apiUrl: string,
  body: CreateBookingBody,
): Promise<{ ok: true; booking: BookingResult } | { ok: false; offline: boolean; error: string }> {
  const { ok, offline, data } = await postBooking(apiUrl, "", body);
  return ok
    ? { ok: true, booking: data.booking as BookingResult }
    : { ok: false, offline, error: (data.error as string) ?? "Couldn't complete your booking." };
}

export interface BookingAvailabilityDay {
  /** Local calendar day on the business's timezone, as YYYY-MM-DD. */
  date: string;
  isWorkingDay: boolean;
  slots: { startsAt: string; minute: number; available: boolean }[];
}

/**
 * Bookable times for one instant-mode service. Separate from the page payload
 * because it goes stale the moment anyone books.
 */
export async function fetchBookingAvailability(
  apiUrl: string,
  slug: string,
  serviceId: string,
  days = 14,
): Promise<
  { ok: true; timeZone: string; days: BookingAvailabilityDay[] } | { ok: false; error: string }
> {
  try {
    const url =
      `${apiUrl}/api/booking/public/${encodeURIComponent(slug)}/availability` +
      `?serviceId=${encodeURIComponent(serviceId)}&days=${days}`;
    const res = await fetch(url);
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      return { ok: false, error: (json.error as string) ?? "Couldn't load available times." };
    }
    return {
      ok: true,
      timeZone: json.timeZone as string,
      days: (json.days as BookingAvailabilityDay[]) ?? [],
    };
  } catch {
    return { ok: false, error: "Couldn't load available times." };
  }
}
